import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../auth/store";
import { useCan } from "../../../auth/roles";
import { ErrorState, Skeleton } from "../../../components/States";
import { SaveBar, UnsavedGuard, settingsEqual } from "../../../components/UnsavedChanges";
import { BusyButton, ConfirmDialog, DataTable, Dialog, Field, PageHeader, RowMenu, Switch, useToast } from "../../../components/ui";
import { backendApi, backendKeys, type BackendPlan, type BackendSettings, type CaptureLevel, type CaptureOverride, type RedactionRule } from "../api";
import { BackToBackend } from "../components";

const LEVELS: Array<{ id: CaptureLevel; label: string; hint: string }> = [
  { id: "everything", label: "Everything", hint: "Every request and every log line, including debug." },
  { id: "standard", label: "Standard", hint: "Every request, and info logs and above. Recommended." },
  { id: "critical", label: "Problems only", hint: "Only requests that fail, run slow, or come from a replay, with their warnings and errors." },
  { id: "off", label: "Off", hint: "Keep nothing. Services stay connected and can be turned back on here." },
];

const FLOORS = [
  { id: "", label: "Follow the capture level" },
  { id: "debug", label: "Debug and above" },
  { id: "info", label: "Info and above" },
  { id: "warn", label: "Warnings and above" },
  { id: "error", label: "Errors and above" },
  { id: "fatal", label: "Fatal only" },
];

const DETECTORS: Array<{ id: string; label: string }> = [
  { id: "email", label: "Email addresses" },
  { id: "phone", label: "Phone numbers" },
  { id: "card", label: "Card numbers" },
  { id: "ssn", label: "Social Security numbers" },
  { id: "iban", label: "Bank account numbers (IBAN)" },
  { id: "ip", label: "IP addresses" },
  { id: "jwt", label: "Sign-in tokens" },
  { id: "bearer", label: "Authorization headers" },
  { id: "api_key", label: "Secret keys" },
  { id: "url_credentials", label: "Passwords in links" },
];

const ACTIONS = [
  { id: "mask", label: "Replace with [redacted]" },
  { id: "remove", label: "Remove" },
  { id: "hash", label: "Replace with a stable code" },
];

const levelLabel = (l?: string) => LEVELS.find((x) => x.id === l)?.label ?? "Project default";
const floorLabel = (f?: string) => (f ? FLOORS.find((x) => x.id === f)?.label ?? f : "Project default");

interface Draft {
  s: BackendSettings;
  ignore: string;
  headers: string;
  fields: string;
  retention: string;
  budget: string;
  cap: string;
}

const lines = (v: string) => v.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);

function toDraft(s: BackendSettings): Draft {
  return {
    s,
    ignore: (s.ignoreRoutes ?? []).join("\n"),
    headers: (s.headerAllowlist ?? []).join("\n"),
    fields: (s.redaction.fields ?? []).join("\n"),
    retention: s.retentionDays ? String(s.retentionDays) : "",
    budget: s.dailyBudgetGb ? String(s.dailyBudgetGb) : "",
    cap: s.hardCapGb != null ? String(s.hardCapGb) : "",
  };
}

export function fromDraft(d: Draft): BackendSettings {
  const num = (v: string) => (v.trim() === "" ? undefined : Number(v));
  const { boosts: _boosts, ...rest } = d.s;
  return {
    ...rest,
    ignoreRoutes: lines(d.ignore),
    headerAllowlist: lines(d.headers).map((h) => h.toLowerCase()),
    redaction: { ...d.s.redaction, fields: lines(d.fields) },
    retentionDays: num(d.retention),
    dailyBudgetGb: num(d.budget),
    hardCapGb: num(d.cap) ?? null,
  };
}

export function validate(d: Draft, plan?: BackendPlan): Record<string, string> {
  const e: Record<string, string> = {};
  const s = d.s;
  if (!Number.isFinite(s.slowThresholdMs) || s.slowThresholdMs < 50 || s.slowThresholdMs > 600_000) e.slowThresholdMs = "Use 50 ms to 600,000 ms (10 minutes).";
  if (!Number.isInteger(s.seriesPerMetric) || s.seriesPerMetric < 10 || s.seriesPerMetric > 100_000) e.seriesPerMetric = "Use 10 to 100,000.";
  const r = d.retention.trim();
  if (r !== "") {
    const n = Number(r);
    if (!Number.isInteger(n) || n < 1) e.retentionDays = "Use a whole number of days.";
    else if (plan && n > plan.maxRetentionDays) e.retentionDays = `Your plan keeps backend data up to ${plan.maxRetentionDays} days.`;
  }
  for (const [k, v] of [["dailyBudgetGb", d.budget], ["hardCapGb", d.cap]] as const) {
    if (v.trim() !== "" && (!Number.isFinite(Number(v)) || Number(v) < 0)) e[k] = "Use zero or more.";
  }
  return e;
}

export function SettingsPage() {
  const auth = useAuth();
  const can = useCan();
  const pid = auth.currentProjectId;
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: backendKeys.settings(pid), queryFn: () => backendApi.settings(pid!), enabled: !!pid });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saved, setSaved] = useState<Draft | null>(null);
  const [serverErr, setServerErr] = useState<Record<string, string>>({});
  useEffect(() => {
    if (q.data) {
      const d = toDraft(q.data.settings);
      setDraft((cur) => (cur && saved && !settingsEqual(fromDraft(cur), fromDraft(saved)) ? cur : d));
      setSaved(d);
    }
    // Only a fresh load resets the form; saved is read for the comparison.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data]);

  const errors = useMemo(() => ({ ...(draft ? validate(draft, q.data?.plan) : {}), ...serverErr }), [draft, q.data?.plan, serverErr]);
  const dirty = !!draft && !!saved && !settingsEqual(fromDraft(draft), fromDraft(saved));
  const set = (patch: Partial<BackendSettings>) => { setServerErr({}); setDraft((d) => (d ? { ...d, s: { ...d.s, ...patch } } : d)); };
  const setText = (patch: Partial<Omit<Draft, "s">>) => { setServerErr({}); setDraft((d) => (d ? { ...d, ...patch } : d)); };
  const setRedaction = (patch: Partial<BackendSettings["redaction"]>) => set({ redaction: { ...draft!.s.redaction, ...patch } });

  const save = useMutation({
    mutationFn: () => backendApi.saveSettings(pid!, fromDraft(draft!)),
    onSuccess: (res) => {
      const d = toDraft(res.settings);
      setDraft(d);
      setSaved(d);
      qc.setQueryData(backendKeys.settings(pid), res);
      toast.push("success", "Backend settings saved.");
    },
    onError: (e) => {
      const body = (e as { body?: { field?: string } }).body;
      if (body?.field) setServerErr({ [body.field.split(".")[0]!]: (e as Error).message });
      else toast.push("error", (e as Error).message);
    },
  });

  if (!can.admin) {
    return (
      <>
        <PageHeader title="Backend settings" back={<BackToBackend />} />
        <div className="card section"><p className="muted" style={{ margin: 0 }}>An owner or admin manages backend settings.</p></div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Backend settings" back={<BackToBackend />} />
      {q.isError ? (
        <div className="card section"><ErrorState error={q.error} retry={() => void q.refetch()} /></div>
      ) : q.isPending || !draft ? (
        <Skeleton />
      ) : (
        <>
          <UnsavedGuard dirty={dirty} />
          <div className="be-settings">
            <section className="card section form-card" aria-labelledby="be-capture">
              <h2 className="be-h2" id="be-capture">What to keep</h2>
              <div className="be-levels" role="radiogroup" aria-label="Capture level">
                {LEVELS.map((l) => (
                  <label key={l.id} className={`be-level${draft.s.captureLevel === l.id ? " active" : ""}`}>
                    <input type="radio" name="captureLevel" value={l.id} checked={draft.s.captureLevel === l.id} onChange={() => set({ captureLevel: l.id })} autoComplete="off" />
                    <span><strong>{l.label}</strong><small>{l.hint}</small></span>
                  </label>
                ))}
              </div>
              <div className="field-row">
                <Field label="Lowest log level" hint="Log lines below this are dropped.">
                  {(p) => (
                    <select {...p} className="input" value={draft.s.severityFloor ?? ""} onChange={(e) => set({ severityFloor: e.target.value || undefined })}>
                      {FLOORS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </select>
                  )}
                </Field>
                <Field label="Slow request" hint="Milliseconds. Slower requests are always kept." error={errors.slowThresholdMs}>
                  {(p) => <input {...p} className="input" type="number" min={50} max={600000} value={Number.isFinite(draft.s.slowThresholdMs) ? draft.s.slowThresholdMs : ""} onChange={(e) => set({ slowThresholdMs: e.target.value === "" ? NaN : Number(e.target.value) })} />}
                </Field>
              </div>
              <Switch
                checked={draft.s.captureBodies && !draft.s.redaction.phi}
                disabled={!!draft.s.redaction.phi}
                onChange={(v) => set({ captureBodies: v })}
                label="Keep request and response bodies"
                hint={draft.s.redaction.phi ? "Off while protected health information mode is on." : "Bodies are checked for personal data before they are stored."}
              />
            </section>

            <section className="card section form-card" aria-labelledby="be-privacy">
              <h2 className="be-h2" id="be-privacy">Privacy</h2>
              <div className="settings-stack">
                <Switch
                  checked={!!draft.s.redaction.phi}
                  onChange={(v) => set({ redaction: { ...draft.s.redaction, phi: v }, captureBodies: v ? false : draft.s.captureBodies })}
                  label="These services handle protected health information"
                  hint="Bodies are never kept, patient fields are hidden, user IDs are replaced with stable codes, and log messages below error keep only their pattern."
                />
                <Switch
                  checked={!!draft.s.redaction.phi || !!draft.s.redaction.hashUserIds}
                  disabled={!!draft.s.redaction.phi}
                  onChange={(v) => setRedaction({ hashUserIds: v })}
                  label="Replace user IDs with stable codes"
                  hint="The same person keeps the same code, so you can still follow one user's requests."
                />
              </div>
              <fieldset className="be-detectors">
                <legend>Find and hide automatically</legend>
                {DETECTORS.map((d) => {
                  const off = (draft.s.redaction.disabledDetectors ?? []).includes(d.id);
                  return (
                    <label key={d.id}>
                      <input
                        type="checkbox"
                        checked={!off}
                        onChange={(e) => {
                          const cur = new Set(draft.s.redaction.disabledDetectors ?? []);
                          if (e.target.checked) cur.delete(d.id);
                          else cur.add(d.id);
                          setRedaction({ disabledDetectors: [...cur] });
                        }}
                        autoComplete="off"
                      />
                      {d.label}
                    </label>
                  );
                })}
              </fieldset>
              <Field label="More fields to hide" hint="One per line. Matched by name anywhere in a request or log, on top of passwords, tokens, and card fields.">
                {(p) => <textarea {...p} className="input" rows={3} value={draft.fields} onChange={(e) => setText({ fields: e.target.value })} placeholder={"member_number\nloyalty_id"} />}
              </Field>
              <CustomRules rules={draft.s.redaction.custom ?? []} onChange={(custom) => setRedaction({ custom })} error={errors.redaction} />
            </section>

            <section className="card section form-card" aria-labelledby="be-noise">
              <h2 className="be-h2" id="be-noise">Requests</h2>
              <div className="field-row">
                <Field label="Routes to ignore" hint="One per line. End with * to match everything under a path.">
                  {(p) => <textarea {...p} className="input" rows={5} value={draft.ignore} onChange={(e) => setText({ ignore: e.target.value })} placeholder="/healthz" />}
                </Field>
                <Field label="Request headers to keep" hint="One per line. Cookies and authorization are never kept.">
                  {(p) => <textarea {...p} className="input" rows={5} value={draft.headers} onChange={(e) => setText({ headers: e.target.value })} placeholder="x-request-id" />}
                </Field>
              </div>
              <Overrides title="Different rules for a service" kind="service" items={draft.s.services ?? {}} onChange={(services) => set({ services })} />
              <Overrides title="Different rules for an environment" kind="environment" items={draft.s.environments ?? {}} onChange={(environments) => set({ environments })} />
              <Boosts boosts={q.data.settings.boosts ?? []} />
            </section>

            <section className="card section form-card" aria-labelledby="be-metrics">
              <h2 className="be-h2" id="be-metrics">Metrics</h2>
              <Switch checked={draft.s.runtimeMetrics} onChange={(v) => set({ runtimeMetrics: v })} label="Report memory, CPU, and runtime health" hint="Sent by every connected service alongside request rates and response times." />
              <div className="field-row">
                <Field label="Report every">
                  {(p) => (
                    <select {...p} className="input" value={draft.s.metricIntervalSec} onChange={(e) => set({ metricIntervalSec: Number(e.target.value) })}>
                      <option value={10}>10 seconds</option>
                      <option value={30}>30 seconds</option>
                      <option value={60}>1 minute</option>
                    </select>
                  )}
                </Field>
                <Field label="Series per metric" hint={`Beyond this, new label combinations are counted together as “other”. Your plan allows ${q.data.plan.metricSeries.toLocaleString()} in total.`} error={errors.seriesPerMetric}>
                  {(p) => <input {...p} className="input" type="number" min={10} max={100000} value={Number.isFinite(draft.s.seriesPerMetric) ? draft.s.seriesPerMetric : ""} onChange={(e) => set({ seriesPerMetric: e.target.value === "" ? NaN : Number(e.target.value) })} />}
                </Field>
              </div>
            </section>

            <section className="card section form-card" aria-labelledby="be-spend">
              <h2 className="be-h2" id="be-spend">Data and spend</h2>
              <p className="muted small">
                {q.data.plan.usedGb.toFixed(2)} GB used this month of {q.data.plan.includedGb.toLocaleString()} GB included
                {q.data.plan.overagePerGbUsd != null ? `, then $${q.data.plan.overagePerGbUsd.toFixed(2)} per GB` : ""}.
              </p>
              <div className="field-row">
                <Field label="Keep data for" hint={`Days. Up to ${q.data.plan.maxRetentionDays} on your plan.`} error={errors.retentionDays}>
                  {(p) => <input {...p} className="input" type="number" min={1} max={q.data.plan.maxRetentionDays} value={draft.retention} placeholder={`Plan default (${q.data.plan.retentionDays} days)`} onChange={(e) => setText({ retention: e.target.value })} />}
                </Field>
                <Field label="Daily limit" hint="GB a day. Past it, only failing requests and errors are kept until midnight UTC." error={errors.dailyBudgetGb}>
                  {(p) => <input {...p} className="input" type="number" min={0} step="0.1" value={draft.budget} placeholder="No daily limit" onChange={(e) => setText({ budget: e.target.value })} />}
                </Field>
                <Field label="Monthly stop" hint="GB a month. Past it, nothing more is kept until next month." error={errors.hardCapGb}>
                  {(p) => <input {...p} className="input" type="number" min={0} step="1" value={draft.cap} placeholder={`Plan default (${Math.round(q.data.plan.hardCapGb).toLocaleString()} GB)`} onChange={(e) => setText({ cap: e.target.value })} />}
                </Field>
              </div>
            </section>

          </div>
          <SaveBar dirty={dirty} saving={save.isPending} disabled={Object.keys(errors).length > 0} onSave={() => save.mutate()} onDiscard={() => { setServerErr({}); if (saved) setDraft(saved); }} />
        </>
      )}
    </>
  );
}

function Overrides({ title, kind, items, onChange }: { title: string; kind: "service" | "environment"; items: Record<string, CaptureOverride>; onChange: (v: Record<string, CaptureOverride>) => void }) {
  const [editing, setEditing] = useState<{ name: string; o: CaptureOverride; original?: string } | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const rows = Object.entries(items).map(([name, o]) => ({ name, o })).sort((a, b) => a.name.localeCompare(b.name));
  const noun = kind === "service" ? "Service" : "Environment";
  const taken = editing && editing.name.trim() !== editing.original && Object.hasOwn(items, editing.name.trim());
  const ready = !!editing && editing.name.trim() !== "" && !taken && !!(editing.o.captureLevel || editing.o.severityFloor || editing.o.captureBodies !== undefined);
  return (
    <div className="be-sub">
      <div className="be-sub-head">
        <h3>{title}</h3>
        <button type="button" className="btn" onClick={() => setEditing({ name: "", o: {} })}>Add rule</button>
      </div>
      {rows.length > 0 && (
        <div className="stack-table be-table">
          <DataTable
            ariaLabel={title}
            rows={rows}
            rowKey={(r) => r.name}
            columns={[
              { key: "name", header: noun, label: noun, render: (r) => <strong>{r.name}</strong> },
              { key: "lvl", header: "Keep", label: "Keep", render: (r) => levelLabel(r.o.captureLevel || undefined) },
              { key: "floor", header: "Lowest log level", label: "Lowest log level", render: (r) => floorLabel(r.o.severityFloor) },
              { key: "body", header: "Bodies", label: "Bodies", render: (r) => (r.o.captureBodies === undefined ? "Project default" : r.o.captureBodies ? "Kept" : "Not kept") },
              {
                key: "act",
                header: "",
                align: "right",
                render: (r) => (
                  <RowMenu label={`Actions for ${r.name}`}>
                    <button type="button" role="menuitem" onClick={() => setEditing({ name: r.name, o: { ...r.o }, original: r.name })}>Edit rule</button>
                    <button type="button" role="menuitem" className="danger" onClick={() => setRemoving(r.name)}>Remove rule</button>
                  </RowMenu>
                ),
              },
            ]}
          />
        </div>
      )}
      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.original ? `Edit rule for ${editing.original}` : `Add a rule for one ${kind}`}
        width={520}
        className="be-editor"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setEditing(null)}>Cancel</button>
            <button
              type="button"
              className="btn primary"
              disabled={!ready}
              onClick={() => {
                if (!editing) return;
                const next = { ...items };
                if (editing.original) delete next[editing.original];
                const o: CaptureOverride = {};
                if (editing.o.captureLevel) o.captureLevel = editing.o.captureLevel;
                if (editing.o.severityFloor) o.severityFloor = editing.o.severityFloor;
                if (editing.o.captureBodies !== undefined) o.captureBodies = editing.o.captureBodies;
                next[editing.name.trim()] = o;
                onChange(next);
                setEditing(null);
              }}
            >
              {editing?.original ? "Update rule" : "Add rule"}
            </button>
          </>
        }
      >
        {editing && (
          <>
            <Field label={noun} error={taken ? `${editing.name.trim()} already has a rule.` : undefined}>
              {(p) => <input {...p} className="input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder={kind === "service" ? "checkout" : "staging"} />}
            </Field>
            <Field label="Keep">
              {(p) => (
                <select {...p} className="input" value={editing.o.captureLevel ?? ""} onChange={(e) => setEditing({ ...editing, o: { ...editing.o, captureLevel: (e.target.value || undefined) as CaptureLevel | undefined } })}>
                  <option value="">Project default</option>
                  {LEVELS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                </select>
              )}
            </Field>
            <Field label="Lowest log level">
              {(p) => (
                <select {...p} className="input" value={editing.o.severityFloor ?? ""} onChange={(e) => setEditing({ ...editing, o: { ...editing.o, severityFloor: e.target.value || undefined } })}>
                  {FLOORS.map((f) => <option key={f.id} value={f.id}>{f.id ? f.label : "Project default"}</option>)}
                </select>
              )}
            </Field>
            <Field label="Request and response bodies">
              {(p) => (
                <select {...p} className="input" value={editing.o.captureBodies === undefined ? "" : editing.o.captureBodies ? "1" : "0"} onChange={(e) => setEditing({ ...editing, o: { ...editing.o, captureBodies: e.target.value === "" ? undefined : e.target.value === "1" } })}>
                  <option value="">Project default</option>
                  <option value="1">Keep</option>
                  <option value="0">Don’t keep</option>
                </select>
              )}
            </Field>
          </>
        )}
      </Dialog>
      <ConfirmDialog
        open={!!removing}
        danger
        typeToConfirm={false}
        title="Remove rule"
        confirmLabel="Remove rule"
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          if (!removing) return;
          const next = { ...items };
          delete next[removing];
          onChange(next);
          setRemoving(null);
        }}
      >
        <p style={{ margin: 0 }}><strong>{removing}</strong> goes back to the project settings once you save.</p>
      </ConfirmDialog>
    </div>
  );
}

function CustomRules({ rules, onChange, error }: { rules: RedactionRule[]; onChange: (v: RedactionRule[]) => void; error?: string }) {
  const [editing, setEditing] = useState<{ rule: RedactionRule; index: number } | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);
  const patternErr = useMemo(() => {
    const p = editing?.rule.pattern?.trim();
    if (!p) return undefined;
    try {
      new RegExp(p);
      return undefined;
    } catch {
      return "This pattern is not valid.";
    }
  }, [editing?.rule.pattern]);
  const r = editing?.rule;
  const dupe = !!r && rules.some((x, i) => i !== editing!.index && x.name.trim().toLowerCase() === r.name.trim().toLowerCase());
  const ready = !!r && r.name.trim() !== "" && !dupe && !!(r.field?.trim() || r.pattern?.trim()) && !patternErr;
  return (
    <div className="be-sub">
      <div className="be-sub-head">
        <h3>Your own hiding rules</h3>
        <button type="button" className="btn" onClick={() => setEditing({ rule: { name: "", action: "mask" }, index: -1 })}>Add hiding rule</button>
      </div>
      {error && <p className="error small" role="alert">{error}</p>}
      {rules.length > 0 && (
        <div className="stack-table be-table">
          <DataTable
            ariaLabel="Hiding rules"
            rows={rules.map((rule, index) => ({ rule, index }))}
            rowKey={(x) => `${x.index}-${x.rule.name}`}
            columns={[
              { key: "name", header: "Name", label: "Name", render: (x) => <strong>{x.rule.name}</strong> },
              { key: "match", header: "Matches", label: "Matches", render: (x) => <span className="mono small">{[x.rule.field && `field ${x.rule.field}`, x.rule.pattern && `text /${x.rule.pattern}/`].filter(Boolean).join(" · ")}</span> },
              { key: "act", header: "Then", label: "Then", render: (x) => ACTIONS.find((a) => a.id === (x.rule.action || "mask"))?.label },
              {
                key: "menu",
                header: "",
                align: "right",
                render: (x) => (
                  <RowMenu label={`Actions for ${x.rule.name}`}>
                    <button type="button" role="menuitem" onClick={() => setEditing({ rule: { ...x.rule }, index: x.index })}>Edit rule</button>
                    <button type="button" role="menuitem" className="danger" onClick={() => setRemoving(x.index)}>Remove rule</button>
                  </RowMenu>
                ),
              },
            ]}
          />
        </div>
      )}
      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing && editing.index >= 0 ? "Edit hiding rule" : "Add hiding rule"}
        width={520}
        className="be-editor"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setEditing(null)}>Cancel</button>
            <button
              type="button"
              className="btn primary"
              disabled={!ready}
              onClick={() => {
                if (!editing) return;
                const clean: RedactionRule = { name: editing.rule.name.trim(), action: editing.rule.action || "mask" };
                if (editing.rule.field?.trim()) clean.field = editing.rule.field.trim();
                if (editing.rule.pattern?.trim()) clean.pattern = editing.rule.pattern.trim();
                const next = [...rules];
                if (editing.index >= 0) next[editing.index] = clean;
                else next.push(clean);
                onChange(next);
                setEditing(null);
              }}
            >
              {editing && editing.index >= 0 ? "Update rule" : "Add rule"}
            </button>
          </>
        }
      >
        {editing && r && (
          <>
            <Field label="Name" error={dupe ? "Another rule already has this name." : undefined}>
              {(p) => <input {...p} className="input" value={r.name} onChange={(e) => setEditing({ ...editing, rule: { ...r, name: e.target.value } })} placeholder="Loyalty numbers" />}
            </Field>
            <Field label="Field name" hint="Optional. Use * as a wildcard, as in user.* or *.card_holder.">
              {(p) => <input {...p} className="input mono" value={r.field ?? ""} onChange={(e) => setEditing({ ...editing, rule: { ...r, field: e.target.value } })} placeholder="*.loyalty_id" />}
            </Field>
            <Field label="Text pattern" hint="Optional. A regular expression found anywhere in a value or log message." error={patternErr}>
              {(p) => <input {...p} className="input mono" value={r.pattern ?? ""} onChange={(e) => setEditing({ ...editing, rule: { ...r, pattern: e.target.value } })} placeholder="LY-\d{8}" />}
            </Field>
            <Field label="Then">
              {(p) => (
                <select {...p} className="input" value={r.action || "mask"} onChange={(e) => setEditing({ ...editing, rule: { ...r, action: e.target.value } })}>
                  {ACTIONS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
              )}
            </Field>
          </>
        )}
      </Dialog>
      <ConfirmDialog
        open={removing !== null}
        danger
        typeToConfirm={false}
        title="Remove hiding rule"
        confirmLabel="Remove hiding rule"
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          if (removing === null) return;
          onChange(rules.filter((_, i) => i !== removing));
          setRemoving(null);
        }}
      >
        <p style={{ margin: 0 }}><strong>{removing !== null ? rules[removing]?.name : ""}</strong> stops hiding values once you save.</p>
      </ConfirmDialog>
    </div>
  );
}

function Boosts({ boosts }: { boosts: Array<{ service?: string; until: string }> }) {
  const auth = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const live = boosts.filter((b) => new Date(b.until).getTime() > Date.now());
  const end = useMutation({
    mutationFn: (service: string) => backendApi.endBoost(auth.currentProjectId!, service),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: backendKeys.settings(auth.currentProjectId) });
      toast.push("info", "Back to the usual capture level.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  if (live.length === 0) return null;
  return (
    <div className="be-sub">
      <h3>Capturing everything right now</h3>
      <ul className="be-boosts">
        {live.map((b) => (
          <li key={b.service ?? ""}>
            <span><strong>{b.service || "Every service"}</strong> <span className="muted small">until {new Date(b.until).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</span></span>
            <BusyButton className="btn small" busy={end.isPending && end.variables === (b.service ?? "")} busyLabel="Ending…" onClick={() => end.mutate(b.service ?? "")}>End now</BusyButton>
          </li>
        ))}
      </ul>
    </div>
  );
}
